import { useTranslation } from "react-i18next";
import { RiArrowRightSLine } from "@remixicon/react";
import { Button } from "~/components/ui/button";
import { Link } from "~/components/ui/link";
import { Picture } from "~/components/ui/picture";
import { ProductWordmark } from "~/components/products/ProductWordmark";
import type { ProductListFamily } from "~/data/products-list";
import { cn } from "~/lib/utils";
import { ProductRow } from "./ProductRow";
import { ResponsiveText } from "./ResponsiveText";

interface ProductCardFullProps {
  family: ProductListFamily;
  /** Above-the-fold cards (the first two on a phone) load their image eagerly — either can be the LCP element. */
  priority?: boolean;
}

// One product family on /products: hero (image, wordmark, description, Learn More) over its
// purchasable rows. Mobile: everything stacked and centered, a divider between hero and rows.
// md+: image column beside a fixed text column (397px, 570px from lg, 697px from xl), no
// divider. Rows are centered below at md and right-aligned under the text column from lg.
export function ProductCardFull({ family, priority = false }: ProductCardFullProps) {
  const { t } = useTranslation("products");
  const name = t(family.name);
  const { base, md, lg, xl } = family.imageWidths;

  return (
    <article className="border-border flex w-full flex-col items-center gap-3 rounded-3xl border bg-neutral-50/45 p-4 md:gap-0">
      <div className="flex w-full flex-col items-center md:h-[239px] md:flex-row md:items-stretch">
        {/* Figma insets the image frame 3.22% of the hero row's width from the left at every md+
            frame (padding % resolves against the row). Mobile centers it. */}
        <div className="flex justify-center md:flex-1 md:items-center md:justify-start md:pl-[3.22%]">
          <div
            className="relative h-[130px] w-[215px] overflow-hidden md:h-[228px] md:w-[241px] md:rounded-2xl lg:w-[326px] xl:w-[405px]"
            style={
              {
                "--image-w": `${base}px`,
                "--image-w-md": `${md}px`,
                "--image-w-lg": `${lg}px`,
                "--image-w-xl": `${xl}px`,
              } as React.CSSProperties
            }
          >
            <Picture
              src={family.image}
              alt={t(family.imageAlt)}
              sizes={`(min-width: 1280px) ${xl}px, (min-width: 1024px) ${lg}px, (min-width: 768px) ${md}px, ${base}px`}
              className={cn(
                "absolute left-1/2 h-auto w-(--image-w) max-w-none -translate-x-1/2 md:w-(--image-w-md) lg:w-(--image-w-lg) xl:w-(--image-w-xl)",
                family.imageAnchor === "top" ? "top-[-7.5%]" : "top-1/2 -translate-y-1/2",
              )}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : undefined}
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-3 pt-2 md:w-[397px] md:shrink-0 md:justify-center md:py-6 md:pr-6 lg:w-[570px] lg:gap-4 xl:w-[697px]">
          <div className="flex flex-col gap-2">
            <h2>
              <ProductWordmark
                productCode={family.productCode}
                name={name}
                className="text-text-heading h-[22px] md:h-[17px] lg:h-[28px]"
              />
            </h2>
            {/* ResponsiveText can render bare text, which gives the plugin nothing to anchor on here. */}
            <p
              data-sid={`products-list-description:${family.id}`}
              className="text-text-body-alt text-figma-body-sm-size lg:text-figma-body-md-size leading-5 lg:leading-[1.5]"
            >
              <ResponsiveText short={t(family.shortDescription)} long={t(family.description)} />
            </p>
          </div>
          <Button
            variant="link"
            size="sm"
            className="h-auto gap-1 self-end p-0 text-base tracking-[0.16px] underline md:self-start"
            iconRight={<RiArrowRightSLine aria-hidden className="size-6" />}
            asChild
          >
            <Link
              data-sid={`products-list-learn-more:${family.id}`}
              to={family.href}
              aria-label={t("list.learnMoreAria", { name })}
            >
              {t("list.learnMore")}
            </Link>
          </Button>
        </div>
      </div>
      {/* -mb-px: Figma's divider is a zero-height line, so it shouldn't add a pixel to the card. */}
      <hr className="border-border -mb-px w-full md:hidden" />
      <ul className="flex w-full flex-col gap-3 md:max-w-[534px] md:gap-0 lg:w-[616px] lg:max-w-none lg:self-end lg:px-6 lg:pt-3 xl:w-[738px]">
        {family.rows.map((row) => (
          <ProductRow key={row.id} row={row} />
        ))}
      </ul>
    </article>
  );
}
